import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const GSI_SCRIPT = "https://accounts.google.com/gsi/client";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (
            element: HTMLElement,
            options: { type?: string; theme?: string; size?: string; text?: string }
          ) => void;
        };
      };
    };
  }
}

interface GoogleSSOModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the Google ID token (JWT) to send to Bonamassa POST /auth/google. */
  onSelectAccount: (idToken: string) => void;
  /** Web OAuth Client ID (same as Bonamassa GOOGLE_OAUTH_CLIENT_ID). Required for real Google Sign-In. */
  googleClientId: string;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function GoogleSSOModal({
  isOpen,
  onClose,
  onSelectAccount,
  googleClientId,
}: GoogleSSOModalProps) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !googleClientId) return;

    setError(null);
    loadScript(GSI_SCRIPT)
      .then(() => setScriptLoaded(true))
      .catch(() => setError("Não foi possível carregar o Google. Tente novamente."));
  }, [isOpen, googleClientId]);

  useEffect(() => {
    if (!isOpen || !googleClientId || !scriptLoaded || !window.google || !buttonRef.current) return;

    window.google.accounts.id.initialize({
      client_id: googleClientId,
      callback: (response) => {
        onSelectAccount(response.credential);
        onClose();
      },
    });

    // Clear previous button content so we don't duplicate
    buttonRef.current.innerHTML = "";
    window.google.accounts.id.renderButton(buttonRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
    });
  }, [isOpen, googleClientId, scriptLoaded, onSelectAccount, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <svg viewBox="0 0 48 48" className="w-8 h-8">
              <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            <h2 className="text-xl font-medium text-gray-900">Entrar com o Google</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!googleClientId ? (
            <p className="text-sm text-amber-600">
              Login com Google não configurado. Defina GOOGLE_OAUTH_CLIENT_ID (mesmo valor do Bonamassa).
            </p>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Escolha sua conta Google para continuar no akihub.
              </p>
              <div ref={buttonRef} className="flex justify-center min-h-[44px]" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
